import React, { useMemo } from "react";
import { View, type ViewStyle } from "react-native";

import { isWeb } from "@stepapp/core";

import { spacing } from "../../styles";
import type { BlockProps, ComputedProps } from "./types";

const customMapProps = {
  borderRadius: spacing.radius,
  zIndex: 1,
};

const Block = ({
  alignStart,
  alignCenter,
  children,
  justifyCenter,
  justifySpace,
  alignEnd,
  justifyAround,
  justifyEnd,
  row,
  fixed,
  wrap,
  style,
  ...restProps
}: BlockProps) => {
  const styles = useMemo(() => {
    const styleKeys = Object.keys(restProps) as (keyof ComputedProps)[];

    const computedStyles = styleKeys.reduce((acc, key) => {
      const value = restProps[key] as number;

      switch (key) {
        case "height":
        case "width": {
          acc[key] = typeof value === "number" ? value * spacing.unit : `${value}%`;
          break;
        }

        case "flex": {
          acc[key] = value;
          break;
        }

        default: {
          acc[key] = value * (customMapProps[key as "borderRadius"] || spacing.unit);
          break;
        }
      }

      return acc;
    }, {} as ViewStyle);

    return {
      ...computedStyles,
      ...(alignStart && { alignItems: "flex-start" as const }),
      ...(alignCenter && { alignItems: "center" as const }),
      ...(alignEnd && { alignItems: "flex-end" as const }),
      ...(wrap && { flexWrap: "wrap" as const }),
      ...(fixed && { position: isWeb && fixed ? "fixed" : "" }),
      ...(justifyCenter && { justifyContent: "center" as const }),
      ...(row && { flexDirection: "row" as const }),
      ...(justifySpace && { justifyContent: "space-between" as const }),
      ...(justifyAround && { justifyContent: "space-around" as const }),
      ...(justifyEnd && { justifyContent: "flex-end" as const }),
    };
  }, [
    // biome-ignore lint/correctness/useExhaustiveDependencies: TODO: fix this
    restProps,
    alignStart,
    alignCenter,
    alignEnd,
    wrap,
    fixed,
    justifyCenter,
    row,
    justifySpace,
    justifyAround,
    justifyEnd,
  ]);

  return (
    <View {...restProps} style={[styles, style]}>
      {children}
    </View>
  );
};

export default React.memo(Block);
